import { range } from "./utils.ts";
import { roundContract } from "./roundContract.ts";

const bound = (x: number, min: number, max: number) =>
	x < min ? min : x > max ? max : x;

// Replicates core/contractNegotiation/generateContractOptions.ts. A player has
// a preferred contract length (his "smallest amount"); offering more or fewer
// years than that scales the per-year amount up. Used so multiplayer re-sign
// and free-agency demands change with the number of years offered, exactly
// like vanilla BBGM. All money is in thousands (BBGM contract units).
export const contractAmountForYears = ({
	baseAmount,
	desiredExp,
	ovr,
	years,
	season,
	minContractLength,
	maxContractLength,
	minContract,
	maxContract,
}: {
	// Demand at the preferred length (the "smallest amount")
	baseAmount: number;
	// Preferred expiration year (season + preferred length)
	desiredExp: number;
	ovr: number;
	// Offered contract length
	years: number;
	season: number;
	minContractLength: number;
	maxContractLength: number;
	minContract: number;
	maxContract: number;
}): number => {
	// Unknown preferred length (e.g. a league whose database predates the
	// stored expiration): don't scale, just return what he's asking.
	if (!desiredExp || desiredExp <= season) {
		return bound(
			roundContract(baseAmount, minContract),
			minContract,
			maxContract,
		);
	}

	// Deterministic per-player noise from the last digit of ovr (0.10 - 0.19).
	// Parenthesized exactly like generateContractOptions' `growthFactor +=
	// (ovr % 10) * 0.01 - 0.05` - regrouping changes the floating point result
	// and would round some contracts to a different $10k.
	const growthFactor = 0.15 + ((ovr % 10) * 0.01 - 0.05);

	const allowedLengths = range(minContractLength, maxContractLength + 1);

	// Index of the preferred length (the "smallest amount"). During re-sign and
	// free agency the base expiration is just the current season (both phases
	// are after the trade deadline), matching generateContractOptions.
	let found = allowedLengths.findIndex((len) => season + len === desiredExp);
	if (found === -1) {
		found = 0;
	}

	const offered = bound(
		Math.round(years),
		minContractLength,
		maxContractLength,
	);
	const offeredIndex = allowedLengths.indexOf(offered);
	const i = offeredIndex === -1 ? found : offeredIndex;

	const factor = 1 + Math.abs(found - i) * growthFactor;

	// Do the arithmetic in the same order as generateContractOptions (which
	// works in millions and multiplies by 1000 before rounding) so the rounded
	// result is identical down to the last dollar, not just approximately equal.
	return bound(
		roundContract((baseAmount / 1000) * factor * 1000, minContract),
		minContract,
		maxContract,
	);
};
